import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { operationsService } from "../services/operations.service";
import { adminKeys } from "./useAdmin";

/**
 * Staff view of a single transfer plus the manual operations that can move it
 * forward.
 *
 * Every mutation here can affect real customer money, so each one refetches
 * the authoritative backend state rather than optimistically patching the
 * cache - the UI should never show an outcome the backend has not confirmed.
 */
export function useTransferOperations(transferId) {
  const queryClient = useQueryClient();

  const detail = useQuery({
    queryKey: adminKeys.transferDetail(transferId),
    queryFn: () => operationsService.getTransferDetail(transferId),
    enabled: Boolean(transferId),
    // A transfer in flight changes underneath the operator.
    refetchInterval: 15_000,
  });

  function refresh() {
    queryClient.invalidateQueries({
      queryKey: adminKeys.transferDetail(transferId),
    });
    queryClient.invalidateQueries({ queryKey: ["admin", "transfers"] });
  }

  const executeLeg = useMutation({
    mutationFn: (legId) => operationsService.executeLeg(legId),
    onSuccess: refresh,
  });

  const markCompleted = useMutation({
    mutationFn: ({ legId, payload }) =>
      operationsService.markLegCompleted(legId, payload),
    onSuccess: refresh,
  });

  const markFailed = useMutation({
    mutationFn: ({ legId, payload }) =>
      operationsService.markLegFailed(legId, payload),
    onSuccess: refresh,
  });

  const confirmSettlement = useMutation({
    mutationFn: ({ legId, payload }) =>
      operationsService.confirmSettlementFunded(legId, payload),
    onSuccess: refresh,
  });

  const resumeHold = useMutation({
    mutationFn: (payload = {}) =>
      operationsService.resumeFundingHold(transferId, payload),
    onSuccess: refresh,
  });

  return {
    transfer: detail.data ?? null,
    isLoading: detail.isLoading,
    error: detail.error,
    refetch: detail.refetch,
    isFetching: detail.isFetching,

    executeLeg: executeLeg.mutateAsync,
    markLegCompleted: markCompleted.mutateAsync,
    markLegFailed: markFailed.mutateAsync,
    confirmSettlementFunded: confirmSettlement.mutateAsync,
    resumeFundingHold: resumeHold.mutateAsync,

    isActing:
      executeLeg.isPending ||
      markCompleted.isPending ||
      markFailed.isPending ||
      confirmSettlement.isPending ||
      resumeHold.isPending,
    actionError:
      executeLeg.error ||
      markCompleted.error ||
      markFailed.error ||
      confirmSettlement.error ||
      resumeHold.error,
  };
}

/** Provider-wide reconciliation sweeps, surfaced on the transfers list. */
export function useReconciliationSweeps() {
  const queryClient = useQueryClient();
  const onSuccess = () =>
    queryClient.invalidateQueries({ queryKey: ["admin"] });

  const bridge = useMutation({
    mutationFn: () => operationsService.reconcileBridgeOnramps(),
    onSuccess,
  });
  const zuba = useMutation({
    mutationFn: () => operationsService.reconcileZubaOnramps(),
    onSuccess,
  });

  return {
    reconcileBridge: bridge.mutateAsync,
    reconcileZuba: zuba.mutateAsync,
    isSweeping: bridge.isPending || zuba.isPending,
    sweepError: bridge.error || zuba.error,
    sweepResult: bridge.data || zuba.data,
  };
}
